import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, TrendingUp, Users, Building2, DollarSign, CheckCircle, Home, Wrench, Landmark } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

// Cleveland market stats (can be updated with real data)
const clevelandStats = {
  avgHomePrice: '$125,000',
  avgRent: '$1,200',
  capRate: '8-12%',
  population: '2.1M metro',
  growth: '+15% investor activity',
  activeDeals: '50+'
};

export default function ClevelandInvestorsPage() {
  const benefits = [
    {
      icon: TrendingUp,
      title: 'High Cash Flow Market',
      description: 'Cleveland offers some of the best cap rates in the country with average returns of 8-12% on rental properties.'
    },
    {
      icon: DollarSign,
      title: 'Affordable Entry Points',
      description: `Average home prices around ${clevelandStats.avgHomePrice} mean you can build a portfolio faster than coastal markets.`
    },
    {
      icon: Building2,
      title: 'Diverse Property Types',
      description: 'Single family, duplexes, triplexes, and multi-family properties across Cleveland neighborhoods.'
    },
    {
      icon: Users,
      title: 'Strong Rental Demand',
      description: `${clevelandStats.population} metro population with steady rental demand and average rents of ${clevelandStats.avgRent}/month.`
    }
  ];

  const neighborhoods = [
    'Lakewood', 'Ohio City', 'Tremont', 'Detroit Shoreway', 
    'Old Brooklyn', 'Parma', 'Euclid', 'Cleveland Heights'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/2923595/pexels-photo-2923595.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Cleveland skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="h-5 w-5 text-[#5DADE2]" />
              <span className="text-[#5DADE2] font-medium">Cleveland, Ohio</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Invest in Cleveland Real Estate
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              Discover off-market deals in one of America's best cash flow markets. Connect with local wholesalers and find your next investment property.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-blue-600 text-white rounded-full px-8 text-lg h-14">
                <Link to="/deals?city=Cleveland&state=OH">
                  Browse Cleveland Deals
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 text-lg h-14">
                <Link to="/signup?role=investor">
                  Join Free
                </Link>
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/20">
              <div>
                <div className="text-2xl font-bold text-white">{clevelandStats.activeDeals}</div>
                <div className="text-sm text-slate-400">Active Deals</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{clevelandStats.capRate}</div>
                <div className="text-sm text-slate-400">Avg Cap Rate</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{clevelandStats.growth}</div>
                <div className="text-sm text-slate-400">YoY Growth</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Invest in Cleveland?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The Midwest's hidden gem for real estate investors seeking strong returns.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-[#5DADE2]/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-[#5DADE2]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Popular Investment Neighborhoods
            </h2>
            <p className="text-slate-600">Find deals across Cleveland's most sought-after areas</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {neighborhoods.map((hood, i) => (
              <Link 
                key={i}
                to={`/deals?city=${encodeURIComponent(hood)}&state=OH`}
                className="px-5 py-2.5 bg-white border border-slate-200 rounded-full text-slate-700 hover:border-[#5DADE2] hover:text-[#5DADE2] transition-colors"
              >
                {hood}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Local Resources CTA */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Need Local Help?
            </h2>
            <p className="text-slate-400">Connect with Cleveland's investor-friendly professionals</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/directory?category=Realtor&area=Cleveland" className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors group">
              <Users className="h-10 w-10 text-[#5DADE2] mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Cleveland Realtors</h3>
              <p className="text-slate-400 text-sm mb-4">Investor-friendly agents who know the local market</p>
              <span className="text-[#5DADE2] text-sm font-medium group-hover:underline">Find Realtors →</span>
            </Link>
            
            <Link to="/directory?category=Contractor&area=Cleveland" className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors group">
              <Wrench className="h-10 w-10 text-[#5DADE2] mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Cleveland Contractors</h3>
              <p className="text-slate-400 text-sm mb-4">Reliable contractors for rehabs and repairs</p>
              <span className="text-[#5DADE2] text-sm font-medium group-hover:underline">Find Contractors →</span>
            </Link>
            
            <Link to="/directory?category=Lender&area=Cleveland" className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors group">
              <Landmark className="h-10 w-10 text-[#5DADE2] mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Cleveland Lenders</h3>
              <p className="text-slate-400 text-sm mb-4">Hard money and investment-friendly financing</p>
              <span className="text-[#5DADE2] text-sm font-medium group-hover:underline">Find Lenders →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#5DADE2]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Start Investing in Cleveland Today
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of investors finding deals on NextFlip
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[#5DADE2] hover:bg-blue-50 rounded-full px-8 text-lg h-14">
              <Link to="/signup?role=investor">
                Create Free Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 text-lg h-14">
              <Link to="/deals?city=Cleveland&state=OH">
                Browse Deals
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
